import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Lock, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  // Forgot password form
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Reset password form
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("If that email is registered, a reset link has been sent.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reset email");
    } finally { setSending(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { toast.error("Passwords don't match"); return; }
    setResetting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: newPw });
      setResetDone(true);
      toast.success("Password reset successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally { setResetting(false); }
  };

  // If no token — show forgot password form
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]" data-testid="forgot-password-page">
        <div className="w-full max-w-sm border border-gray-200 bg-white p-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-black flex items-center justify-center">
              <Lock className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight">Reset Password</h1>
              <p className="text-xs text-gray-500 uppercase tracking-[0.15em] font-semibold">Minibar POS</p>
            </div>
          </div>

          {sent ? (
            <div className="text-center py-4">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-sm text-gray-600 mb-4">Check your email for a password reset link.</p>
              <a href="/admin/login" className="text-xs text-gray-400 hover:text-gray-600 uppercase tracking-[0.1em]">
                Back to Login
              </a>
            </div>
          ) : (
            <form onSubmit={handleForgot} className="space-y-4">
              <p className="text-sm text-gray-500 mb-2">Enter your email address and we'll send you a reset link.</p>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Email</Label>
                <Input
                  data-testid="reset-email-input"
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 rounded-none border-gray-300 focus:border-black focus:ring-1 focus:ring-black"
                  autoFocus
                />
              </div>
              <Button data-testid="send-reset-button" type="submit" disabled={sending || !email}
                className="w-full rounded-none bg-black hover:bg-gray-800 text-white font-semibold h-11">
                {sending ? "Sending..." : "Send Reset Link"}
              </Button>
              <div className="text-center">
                <a href="/admin/login" className="text-xs text-gray-400 hover:text-gray-600 uppercase tracking-[0.1em]">
                  <ArrowLeft className="w-3 h-3 inline mr-1" /> Back to Login
                </a>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  // Token present — show reset password form
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]" data-testid="reset-password-page">
      <div className="w-full max-w-sm border border-gray-200 bg-white p-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-black flex items-center justify-center">
            <Lock className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold tracking-tight">New Password</h1>
            <p className="text-xs text-gray-500 uppercase tracking-[0.15em] font-semibold">Minibar POS</p>
          </div>
        </div>

        {resetDone ? (
          <div className="text-center py-4">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <p className="text-sm text-gray-600 mb-4">Your password has been reset.</p>
            <Button onClick={() => navigate("/admin/login")}
              className="rounded-none bg-black hover:bg-gray-800 text-white font-semibold h-11">
              Go to Login
            </Button>
          </div>
        ) : (
          <form onSubmit={handleReset} className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">New Password</Label>
              <Input data-testid="new-pw-input" type="password" value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="mt-1 rounded-none border-gray-300 focus:border-black focus:ring-1 focus:ring-black" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Confirm Password</Label>
              <Input data-testid="confirm-pw-input" type="password" value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className="mt-1 rounded-none border-gray-300 focus:border-black focus:ring-1 focus:ring-black" />
            </div>
            <p className="text-[10px] text-gray-400">Min 12 chars, upper + lower + digit + special character</p>
            <Button data-testid="reset-pw-button" type="submit" disabled={resetting || !newPw || !confirmPw}
              className="w-full rounded-none bg-black hover:bg-gray-800 text-white font-semibold h-11">
              {resetting ? "Resetting..." : "Set New Password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
